/**
 * Design Memory — vocabulary and constants (frozen).
 *
 * The semantic axes the memory understands, the similarity dimensions it
 * scores, their weights, the scope types, and default thresholds. Pure data;
 * no pixels, no embeddings. Memory-specific ordinal vocabularies (density,
 * balance, negative space) live here; the rest of a fingerprint reuses the
 * genome vocabulary.
 */

export const FINGERPRINT_SCHEMA_ID = 'fingerprint/1';
export const FINGERPRINT_VERSION = 1;
export const MEMORY_SCHEMA_ID = 'memory/1';

/** Memory-specific ordinal vocabularies. */
export const VISUAL_DENSITY = Object.freeze(['low', 'medium', 'high']);
export const BALANCE = Object.freeze(['symmetric', 'asymmetric', 'centered']);
export const NEGATIVE_SPACE = Object.freeze(['tight', 'balanced', 'generous']);

/** Memory scopes (tenant + marketplace are future, handled by the same store). */
export const SCOPE_TYPES = Object.freeze(['batch', 'session', 'project', 'tenant', 'marketplace']);

/** The dimensions similarity is scored across (Overall is the weighted blend). */
export const SIMILARITY_DIMENSIONS = Object.freeze([
  'concept', 'composition', 'hierarchy', 'typography', 'color', 'genome',
  'libraryPattern', 'component', 'section', 'visualDensity', 'objectRelationship',
]);

/** Weights for the overall similarity blend (sum = 1). */
export const WEIGHTS = Object.freeze({
  concept: 0.12,
  composition: 0.10,
  hierarchy: 0.10,
  typography: 0.10,
  color: 0.10,
  genome: 0.14,
  libraryPattern: 0.08,
  component: 0.08,
  section: 0.08,
  visualDensity: 0.04,
  objectRelationship: 0.06,
});

/** How each fingerprint field is compared (drives the similarity strategy). */
export const DIMENSION_COMPARE = Object.freeze({
  concept: 'tokens',
  composition: 'categorical',
  hierarchy: 'sequence',
  typography: 'categorical',
  color: 'categorical',
  genome: 'genome',
  libraryPattern: 'set',
  component: 'set',
  section: 'sequence',
  visualDensity: 'ordinal',
  objectRelationship: 'set',
});

/** Fingerprint field backing each similarity dimension. */
export const DIMENSION_FIELD = Object.freeze({
  concept: 'conceptTokens',
  composition: 'composition',
  hierarchy: 'hierarchy',
  typography: 'typography',
  color: 'color',
  genome: 'genome',
  libraryPattern: 'libraryPatterns',
  component: 'componentUsage',
  section: 'sectionOrder',
  visualDensity: 'visualDensity',
  objectRelationship: 'objectRelationships',
});

export const DEFAULT_THRESHOLDS = Object.freeze({
  duplicate: 0.7,       // overall similarity above this ⇒ too similar
  highDimension: 0.7,   // a single dimension above this ⇒ avoid its value
  variationAxis: 0.5,   // a dimension above this ⇒ recommend varying it
});
